import { ClipboardEvent, useCallback, useState } from 'react';
import { getTiktokHashTags, TiktokHashtag } from '@round/api';
import { GenericDropdownOption } from '../../../../App.types';
import { showNotification } from '../../../../helpers';

export type HashtagOption = GenericDropdownOption<number>;

/** Whitespace and commas both separate one tag from the next; neither appears inside one. */
const SEPARATORS = /[\s,]+/;

const mapHashtagToOption = (hashtag: TiktokHashtag): HashtagOption => ({
    value: hashtag.id,
    label: `#${hashtag.title}`,
});

/** Tags are stored bare, so what is pasted as `#Dance` is looked up as `Dance`. */
const splitHashtagTitles = (raw: string) =>
    raw
        .split(SEPARATORS)
        .map((title) => title.replace(/^#/, ''))
        .filter(Boolean);

/**
 * Applied against the selection as it stands when the lookup returns, not as it stood when the
 * paste began: the two differ whenever something is picked while the request is in the air.
 */
export const mergeHashtagOptions = (current: readonly HashtagOption[], added: readonly HashtagOption[]) => {
    const selectedValues = new Set(current.map((option) => option.value));
    return [...current, ...added.filter((option) => !selectedValues.has(option.value))];
};

/**
 * A title is not unique — that is what `superseded_by` exists to reconcile — so a page sized to the
 * titles asked for can fill up on one tag's rows and leave another's unread. Reading to the end
 * costs a second request only when that happens.
 */
const fetchAllByTitle = async (titles: string[]) => {
    const hashtags: TiktokHashtag[] = [];

    for (let page = 1; ; page += 1) {
        const {
            data: { results, next },
        } = await getTiktokHashTags({ title: titles.join(','), page, page_size: titles.length });

        hashtags.push(...results);

        if (!next) {
            return hashtags;
        }
    }
};

type Params = {
    /** Handed every tag the lookup resolved; merging it into the selection is the caller's. */
    onResolved: (options: HashtagOption[]) => void;
};

/**
 * Pasting a list of tags selects every one of them at once, so a report over dozens of hashtags
 * does not mean typing and picking each. A paste of a single token is left to the select's own
 * typeahead: there it is as likely to be the start of a search as a whole tag.
 */
export default function useHashtagPaste({ onResolved }: Params) {
    const [isResolving, setIsResolving] = useState(false);

    const resolve = useCallback(
        async (titles: string[]) => {
            try {
                setIsResolving(true);
                const hashtags = await fetchAllByTitle(titles);

                if (hashtags.length) {
                    onResolved(hashtags.map(mapHashtagToOption));
                }

                // A tag nobody has ever monitored has no row to select, and so no data to report
                // on. Saying which ones beats leaving the paste quietly short.
                const resolved = new Set(hashtags.map((hashtag) => hashtag.title.toLowerCase()));
                const unresolved = titles.filter((title) => !resolved.has(title.toLowerCase()));
                if (unresolved.length) {
                    const names = unresolved.map((title) => `#${title}`).join(', ');
                    showNotification(`No hashtags found for ${names}`, 'warning');
                }
            } catch (e) {
                showNotification(e instanceof Error ? e.message : 'Could not look up pasted hashtags', 'error');
            } finally {
                setIsResolving(false);
            }
        },
        [onResolved]
    );

    const onPaste = useCallback(
        (e: ClipboardEvent) => {
            const titles = splitHashtagTitles(e.clipboardData.getData('text'));
            if (titles.length < 2) {
                return;
            }

            e.preventDefault();
            resolve(titles);
        },
        [resolve]
    );

    return { onPaste, isResolving };
}
